import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { AIEvent } from 'src/app/core/models/websocket/aichat';
import { StructureEvent } from 'src/app/core/models/websocket/structure';
import { FileUploadEvent } from 'src/app/core/models/websocket/fileupload';
import { UserInfo } from './user.service';
import { WorkspacesEvent } from 'src/app/layout/sidebar/workspace-manager/workspace-manager.events';

@Injectable({
    providedIn: 'root',
})
export class VisibilityObservable {
    private inFocus: boolean = document.hidden;
    private inFocusSubject = new BehaviorSubject<boolean>(this.inFocus);
    public inFocus$ = this.inFocusSubject.asObservable();

    constructor() {
        document.addEventListener('visibilitychange', (e) => {
            if (!document.hidden) {
                // Tab is in focus
                this.inFocus = !document.hidden;
            } else {
                // Tab is inactive
                this.inFocus = !document.hidden;
            }
            this.inFocusSubject.next(this.inFocus);
        });
    }

    isActive() {
        return this.inFocus;
    }
}

@Injectable({
    providedIn: 'root',
})
export class StateObservable {
    private loading: boolean = false;

    constructor() {}

    private loadingSubject = new BehaviorSubject<boolean>(false);
    public loading$ = this.loadingSubject.asObservable();

    setLoading(loading: boolean = false) {
        this.loadingSubject.next(loading);
    }
}

@Injectable({
    providedIn: 'root',
})
export class AIChatObservable {
    constructor() {}

    private messageSubject: Subject<AIEvent> = new Subject<AIEvent>();

    registerNewUpdate(data: AIEvent) {
        this.messageSubject.next(data);
    }

    updates(): Observable<AIEvent> {
        return this.messageSubject.asObservable();
    }
}

@Injectable({
    providedIn: 'root',
})
export class StructureObservable {
    constructor() {}

    private messageSubject: Subject<StructureEvent> =
        new Subject<StructureEvent>();

    registerNewUpdate(data: StructureEvent) {
        this.messageSubject.next(data);
    }

    updates(): Observable<StructureEvent> {
        return this.messageSubject.asObservable();
    }
}

@Injectable({
    providedIn: 'root',
})
export class FileUploadObservable {
    constructor() {}

    private messageSubject: Subject<FileUploadEvent> =
        new Subject<FileUploadEvent>();

    registerNewUpdate(data: FileUploadEvent) {
        this.messageSubject.next(data);
    }

    updates(): Observable<FileUploadEvent> {
        return this.messageSubject.asObservable();
    }
}

@Injectable({
    providedIn: 'root',
})
export class CreditCountObservable {
    constructor() {}

    private creditCount: number = 0; // Number of credits still available to the user
    private messageSubject: Subject<number> = new Subject<number>();

    registerNewUpdate(data: number) {
        if (data != this.creditCount) {
            this.creditCount = data;
            this.messageSubject.next(data);
        }
    }

    updates(): Observable<number> {
        return this.messageSubject.asObservable();
    }

    get(): number {
        return this.creditCount;
    }

    clear(): void {
        this.creditCount = 0;
        this.messageSubject.next(0);
    }
}

export class SidebarToggleObservable {
    constructor() {}

    private toggledSubject = new BehaviorSubject<boolean>(false);
    public toggled$ = this.toggledSubject.asObservable();

    toggleSidebar() {
        this.toggledSubject.next(!this.toggledSubject.value);
    }

    showSidebar() {
        this.toggledSubject.next(true);
    }

    hideSidebar() {
        this.toggledSubject.next(false);
    }
}

@Injectable({
    providedIn: 'root',
})
export class AIAudioInputObservable {
    constructor() {}

    private audioSubject = new BehaviorSubject<Blob | null>(null);
    public audioSubject$ = this.audioSubject.asObservable();

    clear() {
        this.audioSubject.next(null);
    }

    add(audio: Blob) {
        this.audioSubject.next(audio);
    }
}

export class ActiveSheetObservable {
    constructor() {}

    private sheetSubject = new BehaviorSubject<string>('');
    private tableSubject = new BehaviorSubject<string>('');
    private tableNameSubject = new BehaviorSubject<string>('');

    public sheetId$ = this.sheetSubject.asObservable();
    public tableId$ = this.tableSubject.asObservable();
    public tableName$ = this.tableNameSubject.asObservable();

    setActiveSheet(sheetId: string = '', tableId: string = '') {
        if (sheetId !== this.sheetSubject.value) {
            console.log(`Sheet is now '${sheetId}' (Table: '${tableId}')`);
            this.sheetSubject.next(sheetId);
        }
    }

    getActiveSheet(): string {
        return this.sheetSubject.value;
    }

    getActiveTable(): string {
        return this.tableSubject.value;
    }

    setActiveTable(tableId: string = '') {
        if (tableId !== this.tableSubject.value) {
            console.log('Setting active table to:', tableId);
            this.tableSubject.next(tableId);
            this.sheetSubject.next(''); // Clear the active sheet
        }
    }

    getActiveTableName(): string {
        return this.tableNameSubject.value;
    }

    setActiveTableName(name: string = '') {
        this.tableNameSubject.next(name);
    }

    getActive(): { sheetId: string; tableId: string } {
        return {
            sheetId: this.sheetSubject.value,
            tableId: this.tableSubject.value,
        };
    }
}

@Injectable({
    providedIn: 'root',
})
export class SidebarItemCollapseObservable {
    constructor(private sideBarToggleObservable: SidebarToggleObservable) {
        this.toggleSubject.asObservable().subscribe((val) => {
            this.toggleItem(val);
        });
        this.sideBarToggleObservable.toggled$.subscribe((val) => {
            this.toggleSubject.next('');
        });
    }

    private toggleItem(item: 'workspaces' | 'dataspace' | 'integrations' | '') {
        if (item == this.activeSubject.value) {
            this.activeSubject.next('');
            this.showSubject.next(false);
        } else {
            this.activeSubject.next(item);
            this.showSubject.next(true);
        }
    }

    private activeSubject = new BehaviorSubject<
        'workspaces' | 'dataspace' | 'integrations' | ''
    >('');
    private showSubject = new BehaviorSubject<boolean>(false);

    public activeItem$ = this.activeSubject.asObservable();
    public show$ = this.showSubject.asObservable();
    public toggleSubject = new BehaviorSubject<
        'workspaces' | 'dataspace' | 'integrations' | ''
    >('');
}

@Injectable({
    providedIn: 'root',
})
export class UserInfoObservable {
    constructor() {}

    private userInfo: UserInfo = {} as UserInfo;
    private messageSubject: Subject<UserInfo> = new Subject<UserInfo>();

    registerNewUpdate(data: UserInfo) {
        if (!data || Object.keys(data).length === 0) return;
        this.userInfo = data;
        this.messageSubject.next(data);
    }

    updates(): Observable<UserInfo> {
        return this.messageSubject.asObservable();
    }

    get(): UserInfo {
        return this.userInfo;
    }

    clear(): void {
        this.userInfo = {} as UserInfo;
        this.messageSubject.next(this.userInfo);
    }
}

@Injectable({
    providedIn: 'root',
})
export class StateService {
    private correlationId = signal<string>('');

    getCorrelationId() {
        return this.correlationId();
    }

    setCorrelationId(correlationId: string) {
        this.correlationId.set(correlationId);
    }
}

@Injectable({
    providedIn: 'root',
})
export class WorkspacesObservable {
    constructor() {}

    private messageSubject: Subject<WorkspacesEvent> =
        new Subject<WorkspacesEvent>();

    registerNewUpdate(data: WorkspacesEvent) {
        this.messageSubject.next(data);
    }

    updates(): Observable<WorkspacesEvent> {
        return this.messageSubject.asObservable();
    }
}